import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { UploadService } from '@core/services/upload.service';
import { fileToBase64 } from '@core/utils/file-converter';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import * as imageCompression from 'browser-image-compression';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { sentry } from 'sentry';
import { ChangeAvatarService } from './change-avatar.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';


@Component({
  selector: 'change-avatar-dialog',
  templateUrl: './change-avatar.component.html',
  styleUrls: ['./change-avatar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChangeAvatarComponent {
  public isLoading: boolean;
  public selectedPicture: Blob;
  public changeAvatarInput: FormControl;
  public imageChangedEvent: Event;
  public isOnEditDialog: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly meId: string,
    private readonly changeAvatarService: ChangeAvatarService,
    private readonly imageUploadService: UploadService,
    private readonly customMessageService: CustomMessagesService,
    private readonly authenticationService: AuthenticationService,
    private readonly fuseProgressBarService: FuseProgressBarService,
    public readonly matDialogRef: MatDialogRef<ChangeAvatarComponent>,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.isLoading = false;
    this.changeAvatarInput = new FormControl(null, [Validators.required]);
    this.isOnEditDialog = !!(this.meId);
  }

  public async saveNewPicture(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBarService.show();
      this.matDialogRef.disableClose = true;

      const compressedFile = await imageCompression.default(this.selectedPicture, {
        maxSizeMB: 10,
        useWebWorker: true
      });

      const payload = {
        imageBase64Encoded: await fileToBase64(compressedFile),
        filename: this.changeAvatarInput.value.name,
        size: this.changeAvatarInput.value.size,
        path: null
      };

      if (this.isOnEditDialog) {
        payload.path = `users/${this.meId}`;
        await this.updateUserAvatar(payload);
      } else {
        await this.setAvatarToNewUser(payload);
      }
    } catch (err) {
      sentry.captureException(err);
      this.customMessageService.error('CHANGE_AVATAR.MESSAGES.CHANGE_AVATAR_ERROR');
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
      this.matDialogRef.disableClose = false;
    }
  }

  public async imageCropped(event: ImageCroppedEvent): Promise<void> {
    this.selectedPicture = event.file;
  }

  public addFileToInput(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.changeAvatarInput.reset();
    this.changeAvatarInput.markAsTouched();

    const { file, event: newEvent } = this.toInputEvent(event);
    if (!file || !file.name || !file.size) {
      return;
    }

    const allowedImageExtensions = ['jpg', 'jpeg', 'png', 'jfif', 'heic', 'webp'];

    if (!allowedImageExtensions.includes(file.name.split('.').pop())) {
      this.changeAvatarInput.setErrors({ extension: true });
      return;
    }


    const tenMbInBytes = 1024 * 1024 * 10;
    if (file.size > tenMbInBytes) {
      this.changeAvatarInput.setErrors({ size: true });
      return;
    }

    this.changeAvatarInput.setValue(file);
    this.imageChangedEvent = newEvent;
  }

  public async updateUserAvatar(payload): Promise<void> {
    const { path: avatarUrl } = await this.imageUploadService.upload(payload);
    await this.changeAvatarService.updateUser({
      id: this.meId,
      input: { avatarUrl }
    });
    await this.authenticationService.getMe({ updateState: true });
    this.customMessageService.success('CHANGE_AVATAR.MESSAGES.CHANGE_AVATAR_SUCCESS');
    this.matDialogRef.close({
      avatarImagePayload: { imageBase64Encoded: avatarUrl }
    });
  }

  private async setAvatarToNewUser(payload): Promise<void> {
    this.customMessageService.success('CHANGE_AVATAR.MESSAGES.ADD_AVATAR_SUCCESS');
    this.matDialogRef.close({
      avatarImagePayload: payload
    });
  }

  private toInputEvent(event: Event): { file: File, event: Event } {
    const sanitizedEvent = event;
    if (event['dataTransfer']) {
      sanitizedEvent.target['files'] = event['dataTransfer'].files;
      delete sanitizedEvent['dataTransfer'];
    }

    return {
      file: sanitizedEvent.target['files'][0],
      event: sanitizedEvent
    };
  }
}
